<template>
    <Card :title="$t('cardTitles.mainInformation')">
        <div class="card-body">
            <Alert v-if="parsingErrorMessage" type="danger" :dismissible="false">
                {{ parsingErrorMessage }}
            </Alert>
            <div class="row">
                <FormGroup class="col-lg-6" :label="$t('labels.name')" required>
                    <Input v-model="name"/>
                </FormGroup>
                <FormGroup class="col-lg-6" :label="$t('labels.type')" required>
                    <Select
                        disabled
                        :model-value="type"
                        :selected-value="typeLabel"
                        :options="[]"
                        option-key="key"
                        option-value="value"
                    />
                </FormGroup>
                <template v-if="isMarketplace">
                    <FormGroup class="col-lg-6" :label="$t('labels.service')" required>
                        <Select
                            disabled
                            :model-value="serviceValue"
                            :selected-value="serviceLabel"
                            :options="[]"
                            option-key="alias"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-6" :label="$t('labels.api_credentials')">
                        <AppAutoComplete
                            @update:model-value="credential = $event"
                            :url="`/projects/${$route.params.projectUuid}/core/api-credentials`"
                            :model-value="credential?.uuid"
                            :selected-value="credential?.name"
                            :query-params="{
                                service: integration.service
                            }"
                        >
                            <template #optionContent>
                                <span v-if="!credential" class="text-muted">{{ $t('labels.empty') }}</span>
                                <div v-else>
                                    <span class="mr-2">{{ credential.name }}</span>
                                    <span v-if="credential.is_active" class="badge badge-success">Активні</span>
                                    <span v-else class="badge badge-danger">Неактивні</span>
                                </div>
                            </template>
                            <template #optionItem="{option}">
                                <span class="mr-2">{{ option.name }}</span>
                                <span v-if="option.is_active" class="badge badge-success">Активні</span>
                                <span v-else class="badge badge-danger">Неактивні</span>
                            </template>
                        </AppAutoComplete>
                    </FormGroup>
                </template>
                <template v-if="isSupplier">
                    <FormGroup class="col-lg-2" :label="$t('labels.service')" required>
                        <Select
                            disabled
                            :model-value="serviceValue"
                            :selected-value="serviceLabel"
                            :options="[]"
                            option-key="key"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-3" :label="$t('labels.file_url')" required>
                        <Input v-model="fileUrl"/>
                    </FormGroup>
                    <FormGroup class="col-lg-2" :label="$t('labels.unique_product_field')" required>
                        <Select
                            :model-value="uniqueProductField?.key"
                            @update:model-value="uniqueProductField = $event"
                            :options="uniqueProductFieldsOptions"
                            :selected-value="uniqueProductField?.value"
                            option-key="key"
                            option-value="value"
                        />
                    </FormGroup>
                    <FormGroup class="col-lg-2" :label="$t('labels.unique_sku_enabled')">
                        <AppCheckbox v-model="uniqueSkuEnabled"/>
                    </FormGroup>
                    <FormGroup class="col-lg-3" :label="$t('labels.external_source_synced_at')">
                        <InputGroup>
                            <template #prepend>
                                <AppCheckbox :model-value="integration.dropshipper?.auto_parsing_enabled" :title="$t('labels.auto_parsing_enabled')" disabled/>
                            </template>
                            <Input
                                :model-value="integration.dropshipper?.external_source_synced_at"
                                readonly
                            />
                            <template #append>
                                <AppButton
                                    color="light"
                                    @click="updateSupplierProducts"
                                    :disabled="integration.dropshipper?.external_source_synced_at === null"
                                >
                                    <span>{{ $t('buttons.update') }}</span>
                                </AppButton>
                            </template>
                        </InputGroup>
                    </FormGroup>
                </template>
            </div>
        </div>
        <div class="card-footer d-flex justify-content-end flex-wrap" :style="{ gap: '.5rem'}">
            <AppButton color="light" @click="remove">
                <i class="icon-trash mr-2"></i>
                <span>{{ $t('buttons.delete') }}</span>
            </AppButton>
            <AppButton color="teal" @click="$refs.CopyIntegrationModal.show()">
                <i class="icon-copy3 mr-2"></i>
                <span>{{ $t('buttons.copy') }}</span>
            </AppButton>
            <AppButton color="indigo" @click="save">
                <i class="icon-floppy-disk mr-2"></i>
                <span>{{ $t('buttons.edit') }}</span>
            </AppButton>
        </div>
    </Card>
    <CopyIntegrationModal ref="CopyIntegrationModal"/>
</template>
<script>
import Card from '../../../../../js/components/new/CardComponent.vue';
import FormGroup from '../../../../../js/components/new/form/group/FormGroup.vue';
import Input from '../../../../../js/components/new/form/Input.vue';
import Select from '../../../../components/new/form/select/AppSelect.vue';
import InputGroup from '../../../../components/new/form/InputGroup.vue';
import AppCheckbox from '../../../../components/new/form/checkbox/AppCheckbox.vue';
import AppButton from '../../../../../js/components/new/button/AppButton.vue';
import AppAutoComplete from '../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import CopyIntegrationModal from '../sidebarCards/integration/modals/CopyIntegrationModal.vue';
import Alert from '../../../../components/AlertComponent.vue';

import { httpClient } from '../../../../../js/services/HttpClient';
import MessageNotificator from '../../../../../js/services/MessageNotificator';
import { IntegrationServiceEnum } from '../../../enums/IntegrationServiceEnum';
import { UniqueProductFieldEnum } from '../../../enums/UniqueProductFieldEnum';

export default {
    name: "MainInformationCard",
    components: {
        Card, FormGroup, Input, Select, InputGroup, AppCheckbox, AppButton, AppAutoComplete, CopyIntegrationModal, Alert
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
    },
    emits: ['update:integration'],
    data() {
        const uniqueProductFieldsOptions = [
            {key: UniqueProductFieldEnum.SKU, value: this.$t('labels.sku')},
            {key: UniqueProductFieldEnum.SOURCE_ID, value: this.$t('labels.external_id')}
        ]

        const dropshipper = this.integration.dropshipper

        return {
            name: this.integration.name,
            credential: this.integration.api_credentials,
            fileUrl: dropshipper?.external_source_url ?? '',
            uniqueProductFieldsOptions,
            uniqueProductField: uniqueProductFieldsOptions.find((option) => option.key === dropshipper?.unique_product_field) ?? uniqueProductFieldsOptions[0],
            uniqueSkuEnabled: dropshipper?.unique_sku_enabled ?? false
        }
    },
    methods: {
        save() {
            const request = {
                name: this.name
            }

            if (this.isSupplier) {
                request.dropshipper_external_url = this.fileUrl
                request.dropshipper_unique_product_field = this.uniqueProductField.key
                request.dropshipper_unique_sku_enabled = this.uniqueSkuEnabled
            } else {
                request.api_credentials_uuid = this.credential?.uuid
            }

            this.$emit('update:integration', request)
        },
        remove() {
            new MessageNotificator().confirm((this.$t('alerts.delete_integration')), () => {
                httpClient.delete(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.integration.uuid}`).then(() => {
                    this.$router.push({ name: 'IndexIntegrationsPage' })
                })
            })
        },
        async updateSupplierProducts() {
            await httpClient.post(`/dropshippers/${this.integration.dropshipper.uuid}/external-source-sync`).then(() => {
                new MessageNotificator().success(this.$t('alerts.external_source_sync_started'))
            })
        }
    },
    computed: {
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        isMarketplace() {
            return !this.isSupplier
        },
        type() {
            return this.isSupplier ? 'supplier' : 'marketplace'
        },
        typeLabel() {
            return this.$t('labels.type_' + this.type)
        },
        serviceValue() {
            return this.isSupplier ? this.integration.dropshipper?.external_source_service : this.integration.service
        },
        serviceLabel() {
            return this.$t('integrationServices.' + this.serviceValue)
        },
        parsingErrorMessage() {
            const dropshipper = this.integration.dropshipper

            if (!this.isSupplier || dropshipper?.auto_parsing_enabled || !dropshipper?.last_parsing_error) {
                return null
            }

            const key = `alerts.dropshipper_parsing_error_${dropshipper.last_parsing_error}`

            return this.$te(key) ? this.$t(key) : this.$t('alerts.dropshipper_parsing_error_unknown')
        }
    }
}
</script>
